import React from 'react';
import IconOrganization from 'svgIcons/MotionPortalIcons/IconOrganization';
import { NODE_MODIFICATION_TYPE, NODE_TYPE } from './constants';
import { IconPowertrain, IconSiteList, IconSingleAssets } from 'svgIcons/MotionPortalIcons';
import IconLocation from 'svgIcons/MotionPortalIcons/IconLocation';
import { translate } from 'helpers/translateHelper';
import { cloneDeep } from 'lodash';
import { DROP_POSITION } from 'sharedComponents/HierarchyView/constants';
import { getAssetIcon } from 'helpers/icons';

// Transform flat hierarchy data into tree structure for HierarchyView
export const transformHierarchyToTree = (hierarchyData) => {

    const transformLocation = (location, siteId, parentId) => ({
        id: location.locationId,
        name: location.name,
        type: NODE_TYPE.LOCATION,
        parentId: parentId,
        data: {
            type: NODE_TYPE.LOCATION,
            locationId: location.locationId,
            siteId: siteId,
            ...location
        },
        children: [
            // Standalone assets moved to this location
            ...(() => {
                const locationAssets = location.assets || [];
                return locationAssets.length > 0 ? [{
                    id: `singleasset-location-${location.locationId}`,
                    name: `Single assets (${locationAssets.length})`,
                    type: NODE_TYPE.UNASSIGNED_ASSETS,
                    parentId: location.locationId,
                    data: {
                        type: NODE_TYPE.UNASSIGNED_ASSETS,
                        locationId: location.locationId,
                        assetCount: locationAssets.length,
                        assets: locationAssets,
                        siteId: siteId,
                    },
                    children: []
                }] : [];
            })(),
            // Nested powertrains in locations
            ...(location.powertrains || []).map(powertrain => ({
                id: powertrain.powertrainId,
                name: `${powertrain.name} (${(powertrain.assets || []).length})`,
                type: NODE_TYPE.POWERTRAIN,
                parentId: location.locationId,
                data: {
                    type: NODE_TYPE.POWERTRAIN,
                    siteId: siteId,
                    ...powertrain
                },
                children: []
            })),
            // Sublocations
            ...(location.subLocations || []).map(subLocation => transformLocation(subLocation, siteId, location.locationId))
        ]
    });

    const transformSite = (site, parentId) => ({
        id: site.siteId,
        name: site.siteName,
        type: NODE_TYPE.SITE,
        parentId: parentId,
        data: {
            type: NODE_TYPE.SITE,
            ...site
        },
        children: [

            // Not configured assets (single assets)
            ...(() => {
                const notConfiguredAssets = site.notConfiguredAssets || [];
                return notConfiguredAssets.length > 0 ? [{
                    id: `singleasset-site-${site.siteId}`,
                    name: `Single assets (${notConfiguredAssets.length})`,
                    type: NODE_TYPE.UNASSIGNED_ASSETS,
                    parentId: site.siteId,
                    data: {
                        type: NODE_TYPE.UNASSIGNED_ASSETS,
                        siteId: site.siteId,
                        assetCount: notConfiguredAssets.length,
                        assets: notConfiguredAssets
                    },
                    children: []
                }] : [];
            })(),

            // Not configured powertrains
            ...site.notConfiguredPowertrains?.length > 0 ? [{
                id: `unassignedpowertrains-site-${site.siteId}`,
                name: `Powertrains not in hierarchy (${site.notConfiguredPowertrains.length})`,
                type: NODE_TYPE.UNASSIGNED_POWERTRAINS,
                parentId: site.siteId,
                data: {
                    type: NODE_TYPE.UNASSIGNED_POWERTRAINS,
                    siteId: site.siteId,
                    powertrainCount: site.notConfiguredPowertrains.length,
                    powertrains: site.notConfiguredPowertrains
                },
                children: [
                    ...(site.notConfiguredPowertrains || []).map(powertrain => ({
                        id: powertrain.powertrainId,
                        name: `${powertrain.name} (${(powertrain.assets || []).length})`,
                        type: NODE_TYPE.UNASSIGNED_POWERTRAIN,
                        parentId: powertrain.siteId,
                        data: {
                            type: NODE_TYPE.UNASSIGNED_POWERTRAIN,
                            ...powertrain
                        },
                        children: []
                    })),
                ]
            }] : [],

            // Locations in sites
            ...(site.locations || []).map(location => transformLocation(location, site.siteId, site.siteId)),
        ]
    });

    const transformOrganization = (orgData, isSubOrg = false) => {

        return {
            id: orgData.organizationId,
            name: orgData.name,
            type: isSubOrg ? NODE_TYPE.SUB_ORGANIZATION : NODE_TYPE.ORGANIZATION,
            parentId: orgData.parentOrganizationId,
            data: {
                type: isSubOrg ? NODE_TYPE.SUB_ORGANIZATION : NODE_TYPE.ORGANIZATION,
                organizationId: orgData.organizationId,
                ...orgData
            },
            children: [
                // Sub-organizations (recursive)
                ...(orgData.subOrganizations || []).map(subOrg => transformOrganization(subOrg, true)),
                // Sites in this organization
                ...(orgData.sites || []).map(site => transformSite(site, orgData.organizationId)),
            ]
        };
    };

    return hierarchyData.map(orgData => transformOrganization(orgData, false));
};

const addTemporaryInputNode = (treeData, parentId) => {
    const addTempNodeRecursive = (nodes) => {
        return nodes.map(node => {
            if (node.id === parentId) {
                const tempInputNode = {
                    id: `temp-input-${Date.now()}`,
                    name: '',
                    inputEle: true,
                    isTemporary: true,
                    parentId: parentId,
                    data: {
                        type: NODE_TYPE.LOCATION
                    },
                    children: [],
                    placeholder: translate('ABB.Powertrain.Frontend.addLocationplaceholder'),
                };
                return {
                    ...node,
                    children: [...node.children || [], tempInputNode]
                };
            }
            if (node.children) {
                return {
                    ...node,
                    children: addTempNodeRecursive(node.children)
                };
            }
            return node;
        });
    };
    return addTempNodeRecursive(treeData);
};

// Remove temporary input nodes from tree
export const removeTempInputNodes = (nodes) => {
    return nodes
        .filter(node => !node.inputEle) // Remove nodes with inputEle: true
        .map(node => ({
            ...node,
            children: node.children ? removeTempInputNodes(node.children) : []
        }));
};

// --- Private helpers for applyPendingChangesToTree ---

const applyAddChange = (treeData, change) => {
    const { parentId, name, locationId, siteId } = change;
    const newLocationNode = {
        id: locationId,
        name: name,
        type: NODE_TYPE.LOCATION,
        data: { type: NODE_TYPE.LOCATION, locationId, name, sublocations: [], powertrains: [], siteId },
        parentId: parentId,
        children: []
    };
    const addToTreeRecursive = (nodes) => nodes.map(node => {
        if (node.id === parentId) {
            return { ...node, children: [...node.children || [], newLocationNode] };
        }
        if (node.children) {
            return { ...node, children: addToTreeRecursive(node.children) };
        }
        return node;
    });
    return removeTempInputNodes(addToTreeRecursive(treeData));
};

const applyRenameChange = (treeData, change) => {
    const { locationId, name } = change;
    const renameRecursive = (nodes) => nodes.map(node => {
        if (node.id === locationId) {
            return { ...node, name, data: { ...node.data, name } };
        }
        if (node.children) {
            return { ...node, children: renameRecursive(node.children) };
        }
        return node;
    });
    return renameRecursive(treeData);
};

const applyDeleteChange = (treeData, change) => {
    const { locationId } = change;
    const removeRecursive = (nodes) => nodes.filter(node => {
        if (node.id === locationId) { return false; }
        if (node.children) { node.children = removeRecursive(node.children); }
        return true;
    });
    return removeRecursive(treeData);
};

const addPowertrainsToLocationNode = (node, movedNodes, targetNodeId) => {
    const newPowertrainChildren = movedNodes.map(movedNode => ({
        ...movedNode,
        parentId: targetNodeId,
        data: { ...movedNode.data, locationId: targetNodeId, siteId: node.data.siteId }
    }));
    return { ...node, children: [...newPowertrainChildren, ...node.children || []] };
};

const addPowertrainsToSiteNode = (node, movedNodes) => {
    const newPowertrainChildren = movedNodes.map(movedNode => ({
        id: movedNode.id || movedNode.powertrainId,
        name: movedNode.name,
        type: NODE_TYPE.UNASSIGNED_POWERTRAIN,
        parentId: node.id,
        data: { ...movedNode.data, type: NODE_TYPE.UNASSIGNED_POWERTRAIN },
        children: []
    }));
    const existingUnassignedNode = node.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_POWERTRAINS);
    if (existingUnassignedNode) {
        node.children = node.children.map(child =>
            child.data?.type === NODE_TYPE.UNASSIGNED_POWERTRAINS
                ? { ...child, children: [...child.children || [], ...newPowertrainChildren] }
                : child
        );
        return node;
    }
    const newUnassignedPowertrainsNode = {
        id: `unassignedpowertrains-site-${node.id}`,
        name: `Powertrains not in hierarchy (${newPowertrainChildren.length})`,
        type: NODE_TYPE.UNASSIGNED_POWERTRAINS,
        parentId: node.id,
        data: {
            type: NODE_TYPE.UNASSIGNED_POWERTRAINS,
            siteId: node.id,
            powertrainCount: newPowertrainChildren.length,
            powertrains: newPowertrainChildren.map(pt => pt.data)
        },
        children: newPowertrainChildren
    };
    node.children = [...node.children || [], newUnassignedPowertrainsNode];
    return node;
};

const addPowertrainsToNode = (node, movedNodes, targetNodeId, targetType) => {
    if (targetType === NODE_TYPE.LOCATION) {
        return addPowertrainsToLocationNode(node, movedNodes, targetNodeId);
    }
    if (targetType === NODE_TYPE.SITE) {
        return addPowertrainsToSiteNode(node, movedNodes);
    }
    return node;
};

const addOrUpdateUnassignedAssetsNode = (node, newAssetData, newNodeConfig) => {
    const existingNode = node.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS);
    if (existingNode) {
        const updatedAssets = [...existingNode.data.assets || [], ...newAssetData];
        return {
            ...node,
            children: node.children.map(child =>
                child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS
                    ? { ...child, name: `Single assets (${updatedAssets.length})`, data: { ...child.data, assets: updatedAssets, assetCount: updatedAssets.length } }
                    : child
            )
        };
    }
    const newUnassignedNode = {
        ...newNodeConfig,
        name: `Single assets (${newAssetData.length})`,
        type: NODE_TYPE.UNASSIGNED_ASSETS,
        data: { type: NODE_TYPE.UNASSIGNED_ASSETS, ...newNodeConfig.data, assetCount: newAssetData.length, assets: newAssetData },
        children: []
    };
    return { ...node, children: [...node.children || [], newUnassignedNode] };
};

const addAssetsToNode = (node, movedNodes, targetNodeId, targetType) => {
    if (targetType === NODE_TYPE.LOCATION) {
        const newAssetData = movedNodes.map(n => ({ ...n.data, locationId: targetNodeId, siteId: node.data.siteId }));
        return addOrUpdateUnassignedAssetsNode(node, newAssetData, {
            id: `singleasset-location-${targetNodeId}`,
            parentId: targetNodeId,
            data: { locationId: targetNodeId, siteId: node.data.siteId }
        });
    }
    if (targetType === NODE_TYPE.SITE) {
        const newAssetData = movedNodes.map(n => ({ ...n.data, type: NODE_TYPE.ASSET }));
        return addOrUpdateUnassignedAssetsNode(node, newAssetData, {
            id: `singleasset-site-${node.id}`,
            parentId: node.id,
            data: { siteId: node.id }
        });
    }
    return node;
};

const addNodesToTarget = (nodes, targetNodeId, sourceType, targetType, movedNodes) => {
    return nodes.map(node => {
        if (node.id === targetNodeId) {
            if (sourceType === NODE_TYPE.POWERTRAIN) {
                return addPowertrainsToNode(node, movedNodes, targetNodeId, targetType);
            }
            if (sourceType === NODE_TYPE.ASSET) {
                return addAssetsToNode(node, movedNodes, targetNodeId, targetType);
            }
            return { ...node, children: [...node.children || [], { ...movedNodes[0], parentId: targetNodeId }] };
        }
        if (node.children) {
            return { ...node, children: addNodesToTarget(node.children, targetNodeId, sourceType, targetType, movedNodes) };
        }
        return node;
    });
};

const removePowertrainsFromTree = (treeData, powertrainsToRemove, movedNodes) => {
    const removeRecursive = (nodes) => nodes.map(node => {
        if (node.children) {
            node.children = node.children.filter(child => {
                const match = powertrainsToRemove.find(pt => pt.id === child.id || pt.powertrainId === child.id);
                if (match) {
                    movedNodes.push({ ...child, type: NODE_TYPE.POWERTRAIN, data: { ...child.data, type: NODE_TYPE.POWERTRAIN } });
                    return false;
                }
                return true;
            });
            node.children = removeRecursive(node.children);
        }
        return node;
    });
    return removeRecursive(treeData);
};

const removeAssetsFromTree = (treeData, change, movedNodes) => {
    const assetsToRemove = change.assetsData || [change.draggedNode];
    const assetIdsToRemove = new Set(assetsToRemove.map(a => a.id || a.assetId));
    assetsToRemove.forEach(asset => {
        movedNodes.push({ ...asset, type: NODE_TYPE.ASSET, data: { ...asset.data, type: NODE_TYPE.ASSET } });
    });
    const removeRecursive = (nodes) => nodes.map(node => {
        if (node.id === change.parentId && node.data?.assets) {
            const remainingAssets = node.data.assets.filter(asset => !assetIdsToRemove.has(asset.id));
            return {
                ...node,
                name: `Single assets (${remainingAssets.length})`,
                data: { ...node.data, assets: remainingAssets, assetCount: remainingAssets.length }
            };
        }
        if (node.children) {
            return { ...node, children: removeRecursive(node.children) };
        }
        return node;
    });
    return removeRecursive(treeData);
};

const removeLocationNodeFromTree = (treeData, sourceNodeId, movedNodes) => {
    const removeRecursive = (nodes) => nodes.filter(node => {
        if (node.id === sourceNodeId) {
            movedNodes.push({ ...node });
            return false;
        }
        if (node.children) { node.children = removeRecursive(node.children); }
        return true;
    });
    return removeRecursive(treeData);
};

const applyLocationReorderMove = (treeData, change) => {
    const { draggedNode, targetNode, position } = change;
    const sourceNodeId = draggedNode.id;
    const targetNodeId = targetNode.id;
    const targetParentId = targetNode.parentId;
    let movedNode = null;

    const removeRecursive = (nodes) => nodes.filter(node => {
        if (node.id === sourceNodeId) { movedNode = { ...node }; return false; }
        if (node.children) { node.children = removeRecursive(node.children); }
        return true;
    });
    const updatedTreeData = removeRecursive(treeData);

    if (!movedNode || !targetParentId) { return updatedTreeData; }

    const addAsSiblingRecursive = (nodes) => nodes.map(node => {
        if (node.id === targetParentId) {
            const targetIndex = node.children.findIndex(child => child.id === targetNodeId);
            if (targetIndex !== -1) {
                const insertIndex = position === DROP_POSITION.BEFORE ? targetIndex : targetIndex + 1;
                node.children.splice(insertIndex, 0, { ...movedNode, parentId: targetParentId });
            }
            return node;
        }
        if (node.children) {
            return { ...node, children: addAsSiblingRecursive(node.children) };
        }
        return node;
    });
    return addAsSiblingRecursive(updatedTreeData);
};

const applyInsideMove = (treeData, change) => {
    const { draggedNode, targetNode, powertrainsData } = change;
    const sourceType = draggedNode.data?.type || draggedNode.type;
    const targetType = targetNode.data?.type || targetNode.type;
    const movedNodes = [];
    let updatedTreeData = treeData;

    if (sourceType === NODE_TYPE.POWERTRAIN) {
        updatedTreeData = removePowertrainsFromTree(updatedTreeData, powertrainsData || [draggedNode], movedNodes);
    } else if (sourceType === NODE_TYPE.ASSET) {
        updatedTreeData = removeAssetsFromTree(updatedTreeData, change, movedNodes);
    } else {
        updatedTreeData = removeLocationNodeFromTree(updatedTreeData, draggedNode.id, movedNodes);
    }

    if (movedNodes.length > 0) {
        updatedTreeData = addNodesToTarget(updatedTreeData, targetNode.id, sourceType, targetType, movedNodes);
    }
    return updatedTreeData;
};

const applyMoveChange = (treeData, change) => {
    if (!change.draggedNode || !change.targetNode) { return treeData; }
    const sourceType = change.draggedNode.data?.type || change.draggedNode.type;
    const targetType = change.targetNode.data?.type || change.targetNode.type;
    const isLocationReorder = [DROP_POSITION.BEFORE, DROP_POSITION.AFTER].includes(change.position) &&
        sourceType === NODE_TYPE.LOCATION && targetType === NODE_TYPE.LOCATION;
    return isLocationReorder
        ? applyLocationReorderMove(treeData, change)
        : applyInsideMove(treeData, change);
};

// Function to apply pending changes to transformed tree data
export const applyPendingChangesToTree = (treeData, change) => {
    if (!change || change.length === 0) {
        return treeData;
    }

    const updatedTreeData = cloneDeep(treeData);

    switch (change.type) {
        case NODE_MODIFICATION_TYPE.TEMP_INPUT:
            return addTemporaryInputNode(updatedTreeData, change.parentId);
        case NODE_MODIFICATION_TYPE.ADD:
            return applyAddChange(updatedTreeData, change);
        case NODE_MODIFICATION_TYPE.RENAME:
            return applyRenameChange(updatedTreeData, change);
        case NODE_MODIFICATION_TYPE.DELETE:
            return applyDeleteChange(updatedTreeData, change);
        case NODE_MODIFICATION_TYPE.MOVE:
            return applyMoveChange(updatedTreeData, change);
        default:
            return updatedTreeData;
    }
};

// Function to generate all node IDs from transformed hierarchy data for default expansion
export const generateAllNodeIds = (treeData) => {
    const nodeIds = [];

    const traverse = (nodes) => {
        nodes.forEach(node => {
            nodeIds.push(node.id);
            if (node.children && node.children.length > 0) {
                traverse(node.children);
            }
        });
    };

    traverse(treeData);
    return nodeIds;
};

// Helper function to find parent node using nodeMap for efficient lookup
export const findParentNode = (nodeMap, targetNodeId) => {
    const targetNode = nodeMap[targetNodeId];
    if (!targetNode || !targetNode.parentId) {
        return null;
    }
    return nodeMap[targetNode.parentId] || null;
};

// Helper function to calculate the depth of a location node in the hierarchy
export const getLocationDepth = (nodeMap, nodeId) => {
    const node = nodeMap[nodeId];
    if (!node || node.type !== NODE_TYPE.LOCATION) {
        return 0;
    }

    let depth = 1; // Start at 1 for the current location
    let current = node;

    // Traverse up the location hierarchy to count depth
    while (current && current.parentId && nodeMap[current.parentId]?.type === NODE_TYPE.LOCATION) {
        current = nodeMap[current.parentId];
        depth++;
    }

    return depth;
};

// Helper function to find root location of a tree containing the given node
const findRootLocation = (nodeMap, nodeId) => {
    let node = nodeMap[nodeId];
    if (!node) {
        return null;
    }

    // Build the ancestor chain from target to root
    const ancestorChain = [];

    if (node?.type === NODE_TYPE.UNASSIGNED_ASSETS) {
        node = nodeMap[node?.parentId]; // Move up to location level
    }

    let current = node;

    while (current && current.type === NODE_TYPE.LOCATION) {
        ancestorChain.unshift(current); // Add to beginning to maintain root-to-target order
        if (current.parentId && nodeMap[current.parentId]?.type === NODE_TYPE.LOCATION) {
            current = nodeMap[current.parentId];
        } else {
            break;
        }
    }

    if (ancestorChain.length === 0) {
        return null;
    }

    // Build the hierarchy structure from root to target (no siblings)
    const buildHierarchyPath = (ancestors, index = 0) => {
        if (index >= ancestors.length) {
            return null;
        }

        const currentNode = ancestors[index];
        const location = {
            locationId: currentNode.id,
            name: currentNode.name,
            isDeleted: false,
            isModified: false,
            assetIds: null,
            powertrainIds: null,
            subLocations: []
        };

        // If there's a next ancestor (child), add it to subLocations
        if (index + 1 < ancestors.length) {
            const childLocation = buildHierarchyPath(ancestors, index + 1);
            if (childLocation) {
                location.subLocations = [childLocation];
            }
        }

        return location;
    };

    return buildHierarchyPath(ancestorChain);
};

// Helper function to find sibling locations of a given node
export const findSiblingLocations = (nodeMap, nodeId) => {
    const node = nodeMap[nodeId];
    if (!node) {
        return [];
    }

    // Get the parent node first
    const parentNode = nodeMap[node.parentId];
    if (!parentNode) {
        return [];
    }

    // Find all children of the parent (siblings of the target node)
    const siblings = [];

    // Look through parent's children to find sibling locations
    parentNode.children.forEach(childNode => {
        if (childNode.type === NODE_TYPE.LOCATION) {
            siblings.push({
                locationId: childNode.id,
                name: childNode.name,
                isDeleted: false,
                isModified: false,
                assetIds: null,
                powertrainIds: null,
                subLocations: [] // Empty as requested - no children
            });
        }
    });

    return siblings;
};

// Helper function to find the site that contains a node using nodeMap
export const findContainingSite = (nodeMap, nodeId) => {
    const node = nodeMap[nodeId];

    if (node?.data?.siteId) {
        return node.data.siteId;
    }

    return null;
};

// Helper function to check if a node is a descendant of another node
const isDescendantOf = (nodeMap, targetNodeId, ancestorNodeId) => {
    if (!nodeMap || !targetNodeId || !ancestorNodeId) {
        return false;
    }

    let currentNode = nodeMap[targetNodeId];
    while (currentNode && currentNode.parentId) {
        if (currentNode.parentId === ancestorNodeId) {
            return true;
        }
        currentNode = nodeMap[currentNode.parentId];
    }
    return false;
};

// Helper function to check if powertrain has assets in site's assigned locations
const checkPowertrainHasAssetsInSite = (nodeMap, draggedNodes, powertrainId, siteId) => {
    // Find the dragged powertrain to get its assets
    const draggedPowertrain = draggedNodes.find(node =>
        node.id === powertrainId || node.data?.powertrainId === powertrainId
    );

    if (!draggedPowertrain || !draggedPowertrain.data?.assets || draggedPowertrain.data.assets.length === 0) {
        return false; // No assets to check, allow drop
    }

    const powertrainAssets = draggedPowertrain.data.assets;

    // Find the site node in nodeMap
    const siteNodeCandidate = Object.values(nodeMap).find(node =>
        node.data?.type === NODE_TYPE.SITE && node.data?.siteId === siteId
    );

    if (!siteNodeCandidate) {
        return false;
    }

    // Check all locations in the site recursively
    const checkLocationForPowertrainAssets = (locations) => {
        for (const location of locations) {
            if (location.type === NODE_TYPE.LOCATION) {
                // Check if this location has any assets from the powertrain
                if (location.children) {
                    for (const child of location.children) {
                        if (child.data?.assets && child.data.assets.length > 0) {
                            // Check if any of the powertrain's assets exist in this child's assets
                            const hasMatchingAsset = powertrainAssets.some(powertrainAsset =>
                                child.data.assets.some(childAsset =>
                                    powertrainAsset.assetId === childAsset.assetId ||
                                    powertrainAsset.id === childAsset.id
                                )
                            );
                            if (hasMatchingAsset) {
                                return true;
                            }
                        }
                    }
                }
                // Recursively check sublocations
                if (location.children && checkLocationForPowertrainAssets(location.children)) {
                    return true;
                }
            }
        }
        return false;
    };

    return checkLocationForPowertrainAssets(siteNodeCandidate.children || []);
};

// --- Private helpers for canDrop ---

const isPowertrainType = (type) => type === NODE_TYPE.POWERTRAIN;

// Check if any two dragged powertrains share assets with each other
const draggedPowertrainsShareAssets = (draggedNodes) => {
    const powertrainNodes = draggedNodes.filter(n => isPowertrainType(n.data?.type) && n.data?.assets);
    for (const node of powertrainNodes) {
        for (const otherNode of powertrainNodes) {
            if (otherNode === node) { continue; }
            const hasSharedAsset = node.data.assets.some(asset =>
                otherNode.data.assets.some(other => asset.assetId === other.assetId || asset.id === other.id)
            );
            if (hasSharedAsset) { return true; }
        }
    }
    return false;
};

// Classify dragged powertrains as conflicting (assets already in site) or valid
const classifyPowertrainsByConflict = (nodeMap, draggedNodes, targetSiteId) => {
    const conflicting = [];
    const valid = [];
    for (const node of draggedNodes) {
        if (!isPowertrainType(node.data?.type)) { continue; }
        const powertrainId = node.id || node.data?.powertrainId;
        if (checkPowertrainHasAssetsInSite(nodeMap, draggedNodes, powertrainId, targetSiteId)) {
            conflicting.push(node);
        } else {
            valid.push(node);
        }
    }
    return { conflicting, valid };
};

// Update state setters when powertrain conflicts are detected
const applyConflictState = (conflicting, valid, setters) => {
    const { setPartialPowertrainFailure, setHierarchyErrorModal, setSelectedPowertrains, setSaveFailed } = setters;
    setSaveFailed?.(false);
    setHierarchyErrorModal(true);
    if (conflicting.length && valid.length) {
        setPartialPowertrainFailure(true);
    }
    if (setSelectedPowertrains) {
        setSelectedPowertrains(prev => {
            const conflictingIds = conflicting.map(pt => pt.id || pt.data?.powertrainId);
            return prev.filter(id => !conflictingIds.includes(id));
        });
    }
};

// Check external powertrain drops for site conflicts. Returns true (allow), false (deny), or null (no decision)
const checkExternalPowertrainConflicts = (nodeMap, draggedNodes, targetNode, targetType, setters) => {
    const targetSiteId = targetNode?.data?.siteId;
    if (!targetSiteId || targetType !== NODE_TYPE.LOCATION || !draggedNodes.some(n => n.isExternalNode)) {
        return null;
    }
    if (draggedPowertrainsShareAssets(draggedNodes)) {
        setters.setSaveFailed?.(false);
        setters.setHierarchyErrorModal(true);
        return false;
    }
    const { conflicting, valid } = classifyPowertrainsByConflict(nodeMap, draggedNodes, targetSiteId);
    if (conflicting.length > 0) {
        applyConflictState(conflicting, valid, setters);
        return valid.length > 0; // partial drop allowed when some are valid; false when all conflict
    }
    return null;
};

// Check final type-based drop rules
const checkTypeRules = (draggedTypes, targetType) => {
    const validTargets = [NODE_TYPE.SITE, NODE_TYPE.LOCATION];
    if (draggedTypes.includes(NODE_TYPE.LOCATION) && !validTargets.includes(targetType)) { return false; }
    if (draggedTypes.includes(NODE_TYPE.ASSET) && !validTargets.includes(targetType)) { return false; }
    if ([NODE_TYPE.UNASSIGNED_ASSETS, NODE_TYPE.UNASSIGNED_POWERTRAINS].includes(targetType)) { return false; }
    if (draggedTypes.length > 1) { return false; }
    return true;
};

// Function to check if drop is allowed - updated to use nodeMap
export const canDrop = (
    nodeMap,
    draggedNodes,
    targetNode,
    dropPosition,
    setPartialPowertrainFailure,
    setHierarchyErrorModal,
    setSelectedPowertrains,
    setSaveFailed
) => {
    // Prevent dropping parent nodes into their descendants
    if (draggedNodes.some(node => isDescendantOf(nodeMap, targetNode.id, node.id))) {
        return false;
    }

    // Only allow dropping within the same site
    if (draggedNodes[0]?.data?.siteId !== targetNode?.data?.siteId) {
        return false;
    }

    const draggedTypes = [...new Set(draggedNodes.map(node => node.data?.type))];
    const targetType = targetNode.data?.type;

    // Reorder positions are only valid for location-to-location
    if ([DROP_POSITION.AFTER, DROP_POSITION.BEFORE].includes(dropPosition) &&
        (targetType !== NODE_TYPE.LOCATION || !draggedTypes.includes(NODE_TYPE.LOCATION))) {
        return false;
    }

    const hasPowertrainDragged = draggedTypes.some(isPowertrainType);

    // Prevent powertrain-to-powertrain drops
    if (hasPowertrainDragged && isPowertrainType(targetType)) {
        return false;
    }

    // Check external powertrain conflicts when dropping into a location
    if (hasPowertrainDragged) {
        const setters = { setPartialPowertrainFailure, setHierarchyErrorModal, setSelectedPowertrains, setSaveFailed };
        const conflictResult = checkExternalPowertrainConflicts(nodeMap, draggedNodes, targetNode, targetType, setters);
        if (conflictResult !== null) { return conflictResult; }
    }

    return checkTypeRules(draggedTypes, targetType);
};

export const renderIcon = (node) => {
    // Default icon based on node type
    const nodeType = node?.data?.type;
    let IconComponent = null;

    switch (nodeType) {
        case NODE_TYPE.ORGANIZATION:
        case NODE_TYPE.SUB_ORGANIZATION:
            IconComponent = IconOrganization;
            break;
        case NODE_TYPE.SITE:
            IconComponent = IconSiteList;
            break;
        case NODE_TYPE.LOCATION:
            IconComponent = IconLocation;
            break;
        case NODE_TYPE.POWERTRAIN:
        case NODE_TYPE.UNASSIGNED_POWERTRAIN:
            IconComponent = IconPowertrain;
            break;
        case NODE_TYPE.UNASSIGNED_POWERTRAINS:
            IconComponent = IconPowertrain;
            break;
        case NODE_TYPE.UNASSIGNED_ASSETS:
            IconComponent = IconSingleAssets;
            break;
        case NODE_TYPE.ASSET:
            IconComponent = () => getAssetIcon(node?.data?.assetType, { width: '16px', height: '16px' });
            break;
        default:
            return null;
    }

    if (IconComponent) {
        return (
            <span className='hierarchy-node-icon'>
                <IconComponent width={16} height={16} />
            </span>
        );
    }

    return null;
};

// Transform powertrains into node format for HierarchyNode
export const createPowertrainNodes = (powertrainsList) => {

    return powertrainsList.map(powertrain => ({
        id: powertrain.powertrainId || powertrain.id,
        name: powertrain.name,
        type: NODE_TYPE.POWERTRAIN,
        level: 0,
        hasChildren: powertrain?.assets?.length > 0,
        selectable: true,
        draggable: true,
        data: {
            ...powertrain,
            type: NODE_TYPE.POWERTRAIN,
        },
        powertrainId: powertrain.powertrainId || powertrain.id,
        isExternalNode: true,
    }));
};

export const createAssetNodes = (assetsList, siteId, parentId) => {

    return assetsList.map(asset => ({
        id: asset.id || asset.assetId,
        name: asset.name,
        type: NODE_TYPE.ASSET,
        level: 0,
        hasChildren: false,
        selectable: true,
        draggable: true,
        data: {
            ...asset,
            siteId,
            parentId,
            type: NODE_TYPE.ASSET,
        },
        assetId: asset.id || asset.assetId,
        isExternalNode: true,
    }));
};

// Enhanced drop handler that removes external nodes after successful drop
export const createDropHandlerWithCleanup = (handleDrop, setSelectedPowertrains, setSelectedAssets) => {
    return (draggedNodes, targetNode, position) => {
        // Check if any dragged nodes are external nodes (powertrains or assets)
        const externalNodes = draggedNodes.filter(node => node.isExternalNode && [NODE_TYPE.POWERTRAIN, NODE_TYPE.ASSET].includes(node?.data?.type));

        // Call the original drop handler
        const dropResult = handleDrop(draggedNodes, targetNode, position);

        // If drop was successful and we have external nodes, remove them from the list
        if (dropResult !== false && externalNodes.length > 0) {
            setSelectedPowertrains([]);
            setSelectedAssets([]);
        }

        return dropResult;
    };
};

// Clean temporary location IDs from locations array
export const cleanTempLocationIds = (locations) => {
    if (!locations) {
        return;
    }

    locations.forEach(location => {
        // If location ID contains 'location-temp', set it to null
        if (location.locationId && location.locationId.toString().includes('location-temp')) {
            location.locationId = null;
        }

        // Recursively clean sublocations
        if (location.subLocations && location.subLocations.length > 0) {
            cleanTempLocationIds(location.subLocations);
        }
    });
};

// --- Hierarchy utility helpers for createAPIRequestData ---

// Recursively apply an update to a location found by ID in a hierarchy
const updateInHierarchy = (location, targetId, updateFn) => {
    if (location?.locationId === targetId) {
        updateFn(location);
        return true;
    }
    if (location?.subLocations) {
        for (const sub of location.subLocations) {
            if (updateInHierarchy(sub, targetId, updateFn)) { return true; }
        }
    }
    return false;
};

// Remove a location by ID from all sublocation levels
const removeFromHierarchy = (location, nodeId) => {
    if (location?.subLocations && location?.subLocations?.length > 0) {
        location.subLocations = location.subLocations?.filter(sub => sub.locationId !== nodeId);
        location.subLocations.forEach(sub => removeFromHierarchy(sub, nodeId));
    }
};

// Extract (clone) a location from a hierarchy by ID
const extractFromHierarchy = (location, nodeId, markModified = false) => {
    if (location?.locationId === nodeId) {
        return markModified ? { ...location, isModified: true } : { ...location };
    }
    if (location?.subLocations) {
        for (const sub of location.subLocations) {
            const found = extractFromHierarchy(sub, nodeId, markModified);
            if (found) { return found; }
        }
    }
    return null;
};

// Merge root hierarchy into existing hierarchy (handles subLocations, isModified, isDeleted, name, powertrainIds)
const mergeLocationHierarchy = (existing, root, options = {}) => {
    if (!existing || !root) { return; }
    if (options?.copyModified && root?.isModified) {
        existing.isModified = root.isModified;
    }
    if (options?.copyName && root?.isModified) {
        existing.name = root.name;
    }
    if (options?.copyDeleted && root?.isDeleted) {
        existing.isDeleted = root.isDeleted;
    }
    if (options?.copyPowertrainIds && root?.powertrainIds) {
        existing.powertrainIds = root.powertrainIds;
    }
    if (options?.copyAssetIds && root?.assetIds) {
        existing.assetIds = root.assetIds;
    }
    if (options?.copyFreshName && root?.name && root.name !== existing.name) {
        existing.name = root.name;
    }
    if (root?.subLocations) {
        root.subLocations?.forEach(rootSub => {
            const existingSub = (existing.subLocations || []).find(sub => sub.locationId === rootSub.locationId);
            if (existingSub) {
                mergeLocationHierarchy(existingSub, rootSub, options);
            } else {
                if (!existing.subLocations) { existing.subLocations = []; }
                existing.subLocations.push(rootSub);
            }
        });
    }
};

// Returns true if a location entry (or any nested sublocation) carries actual changes
const hasAnyChange = (location) => {
    if (!location) { return false; }
    if (location.isModified || location.isDeleted) { return true; }
    return location.subLocations?.some(sub => hasAnyChange(sub)) ?? false;
};

// A location added during the current edit session has a temporary id and was never
// persisted on the backend, so it carries no real locationId yet
const isNewlyAddedLocationId = (locationId) => {
    return !!locationId && locationId.toString().includes('location-temp');
};

// Returns true if a site entry still carries any change worth sending to the backend
const siteEntryHasChanges = (site) => {
    if (!site) { return false; }
    if (site.isModified) { return true; }
    if (site.unAssignedAssetIds !== null && site.unAssignedAssetIds !== undefined) { return true; }
    if (site.unAssignedPowertrainIds !== null && site.unAssignedPowertrainIds !== undefined) { return true; }
    return site.locations?.some(hasAnyChange) ?? false;
};

// Removes nodeId from every position in siteEntry, then prunes empty entries
const globalPurgeAndClean = (siteEntry, nodeId) => {
    if (!siteEntry?.locations || siteEntry?.locations?.length === 0) { return; }
    siteEntry.locations = siteEntry?.locations.filter(loc => loc.locationId !== nodeId);
    siteEntry.locations.forEach(loc => removeFromHierarchy(loc, nodeId));
    const pruneEmpty = (location) => {
        if (location?.subLocations && location?.subLocations?.length > 0) {
            location.subLocations.forEach(pruneEmpty);
            location.subLocations = location.subLocations.filter(hasAnyChange);
        }
    };
    siteEntry.locations.forEach(pruneEmpty);
    siteEntry.locations = siteEntry.locations.filter(hasAnyChange);
};

// Merge a rootLocation into the correct slot of existingSite.locations
const mergeRootIntoSite = (existingSite, rootLocation, mergeOptions) => {
    if (!existingSite || !rootLocation) { return; }
    const existing = existingSite?.locations?.find(loc => loc.locationId === rootLocation.locationId);
    if (existing) {
        mergeLocationHierarchy(existing, rootLocation, mergeOptions);
    } else {
        existingSite?.locations?.push(rootLocation);
    }
};

// Create a new site entry object
const createSiteEntry = (siteId, siteData, locations, overrides = {}) => ({
    siteId,
    isModified: false,
    rowVersion: siteData?.data?.rowVersion,
    locations,
    unAssignedAssetIds: null,
    unAssignedPowertrainIds: null,
    ...overrides,
});

// Insert movedLocationData at position relative to targetNodeId in a locations array
const insertAtPosition = (locations, movedLocationData, targetNodeId, position) => {
    if (!movedLocationData) { return locations || []; }
    const cleaned = { ...movedLocationData, subLocations: [] };
    const filtered = locations?.filter(sub => sub.locationId !== movedLocationData.locationId);
    const targetIndex = filtered?.findIndex(sub => sub.locationId === targetNodeId);
    if (targetIndex !== -1) {
        const insertIdx = position === DROP_POSITION.BEFORE ? targetIndex : targetIndex + 1;
        filtered.splice(insertIdx, 0, cleaned);
    }
    return filtered;
};

// Build combined locations array from source/target root locations
const buildLocationsList = (sourceRootLocation, targetRootLocation, extraLocation) => {
    const locations = [];
    if (sourceRootLocation && targetRootLocation) {
        if (sourceRootLocation.locationId === targetRootLocation.locationId) {
            locations.push(targetRootLocation);
        } else {
            locations.push(sourceRootLocation, targetRootLocation);
        }
    } else if (sourceRootLocation) {
        locations.push(sourceRootLocation);
        if (extraLocation) { locations.push(extraLocation); }
    } else if (targetRootLocation) {
        // The moved location (extraLocation) is already nested inside targetRootLocation, so it
        // must NOT be pushed again as a top-level entry. Re-adding it duplicates the location in
        // the payload (e.g. moving a top-level location into another location) and, because the
        // nested and top-level copies share the same object reference, a later move under it
        // corrupts both. The backend then rejects the duplicate with an "item with the same key"
        // error.
        locations.push(targetRootLocation);
    }
    return locations;
};

// --- Handler: ADD ---
const handleAdd = (change, nodeMap, modifiedSites, siteData, existingSite) => {
    const rootLocation = findRootLocation(nodeMap, change.parentId);
    const parentData = nodeMap[change.parentId];
    const newLocation = {
        locationId: change.locationId,
        name: change.name,
        isDeleted: false,
        isModified: true,
        assetIds: null,
        powertrainIds: null,
        subLocations: []
    };

    if (rootLocation && parentData?.data?.type !== NODE_TYPE.SITE) {
        updateInHierarchy(rootLocation, change.parentId, (loc) => {
            loc.subLocations.push(newLocation);
        });
    }

    if (existingSite) {
        if (rootLocation) {
            mergeRootIntoSite(existingSite, rootLocation, {});
        } else {
            if (!existingSite.locations) { existingSite.locations = []; }
            existingSite.locations.push(newLocation);
        }
    } else {
        modifiedSites.push(createSiteEntry(change.siteId, siteData, rootLocation ? [rootLocation] : [newLocation]));
    }
};

// --- Handler: RENAME ---
const handleRename = (change, nodeMap, modifiedSites, siteData, existingSite) => {
    const rootLocation = findRootLocation(nodeMap, change.locationId);

    if (rootLocation) {
        updateInHierarchy(rootLocation, change.locationId, (loc) => {
            loc.name = change.name;
            loc.isModified = true;
        });
    }

    if (existingSite) {
        mergeRootIntoSite(existingSite, rootLocation, { copyModified: true, copyName: true });
    } else {
        modifiedSites.push(createSiteEntry(change.siteId, siteData, rootLocation ? [rootLocation] : []));
    }
};

// --- Handler: DELETE ---
const handleDelete = (change, nodeMap, modifiedSites, siteData, existingSite) => {
    // A location that was newly added and then deleted before saving never existed on the
    // backend. Marking it deleted would push a stale entry (locationId: null, isDeleted: true,
    // isModified: true) into the payload and keep the save button enabled. Instead, remove it
    // from the payload entirely and drop the site entry when nothing else changed for it.
    if (isNewlyAddedLocationId(change.locationId)) {
        if (existingSite) {
            globalPurgeAndClean(existingSite, change.locationId);
            if (!siteEntryHasChanges(existingSite)) {
                const siteIndex = modifiedSites.findIndex(site => site.siteId === existingSite.siteId);
                if (siteIndex !== -1) {
                    modifiedSites.splice(siteIndex, 1);
                }
            }
        }
        return;
    }

    const rootLocation = findRootLocation(nodeMap, change.locationId);

    if (rootLocation) {
        updateInHierarchy(rootLocation, change.locationId, (loc) => {
            loc.isDeleted = true;
        });
    }

    if (existingSite) {
        mergeRootIntoSite(existingSite, rootLocation, { copyDeleted: true });
    } else {
        modifiedSites.push(createSiteEntry(change.siteId, siteData, rootLocation ? [rootLocation] : []));
    }
};

// Handle site-level reordering when targetRootLocation is null (BEFORE/AFTER at site level)
const handleSiteLevelReorder = (existingSite, nodeMap, targetNodeId, movedLocationData, position) => {
    const siteLocations = findSiblingLocations(nodeMap, targetNodeId);
    const mergedLocations = siteLocations.map(freshLocation => {
        const existingLocation = existingSite.locations?.find(loc => loc.locationId === freshLocation.locationId);
        if (existingLocation) {
            mergeLocationHierarchy(existingLocation, freshLocation, { copyModified: true, copyFreshName: true });
            return existingLocation;
        }
        return freshLocation;
    });

    existingSite.locations = insertAtPosition(mergedLocations, movedLocationData, targetNodeId, position);
    existingSite.isModified = true;
};

// Build locations array for new site entry in BEFORE/AFTER move
const buildBeforeAfterLocations = (sourceRootLocation, targetRootLocation, movedLocationData, nodeMap, targetNodeId, position) => {
    if (sourceRootLocation && targetRootLocation) {
        return sourceRootLocation.locationId === targetRootLocation.locationId
            ? [targetRootLocation]
            : [sourceRootLocation, targetRootLocation];
    }
    if (sourceRootLocation) {
        const siteLocations = findSiblingLocations(nodeMap, targetNodeId);
        return insertAtPosition(siteLocations, movedLocationData, targetNodeId, position);
    }
    if (targetRootLocation) {
        return [targetRootLocation, movedLocationData];
    }
    return [];
};

// --- Handler: MOVE with BEFORE/AFTER position ---
const handleMoveBeforeAfter = (change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation) => {
    const { sourceNodeId, targetNodeId, targetNode: targetNodeData, position } = change;

    let movedLocationData = null;
    if (sourceRootLocation) {
        movedLocationData = extractFromHierarchy(sourceRootLocation, sourceNodeId, true);
        removeFromHierarchy(sourceRootLocation, sourceNodeId);
    }

    if (nodeMap[targetNodeData?.parentId]?.data?.type === NODE_TYPE.SITE) {
        targetRootLocation = null;
    }

    if (targetRootLocation && movedLocationData) {
        updateInHierarchy(targetRootLocation, targetNodeData?.parentId, (location) => {
            location.subLocations = findSiblingLocations(nodeMap, targetNodeId);
            location.subLocations = insertAtPosition(location.subLocations, movedLocationData, targetNodeId, position);
            location.isModified = true;
        });
    }

    if (existingSite) {
        globalPurgeAndClean(existingSite, sourceNodeId);

        if (targetRootLocation) {
            mergeRootIntoSite(existingSite, targetRootLocation, { copyModified: true });
        } else {
            handleSiteLevelReorder(existingSite, nodeMap, targetNodeId, movedLocationData, position);
        }
    } else {
        const locations = buildBeforeAfterLocations(
            sourceRootLocation, targetRootLocation, movedLocationData, nodeMap, targetNodeId, position
        );
        modifiedSites.push(createSiteEntry(change.siteId, siteData, locations, { isModified: !targetRootLocation }));
    }
};

// Applies move-location changes when site already exists in modifiedSites
const applyMoveLocationToExistingSite = (existingSite, sourceNodeId, sourceRootLocation, targetRootLocation, movedLocationData) => {
    globalPurgeAndClean(existingSite, sourceNodeId);
    if (sourceRootLocation && hasAnyChange(sourceRootLocation)) {
        mergeRootIntoSite(existingSite, sourceRootLocation, { copyModified: true });
    }
    if (targetRootLocation) {
        mergeRootIntoSite(existingSite, targetRootLocation, { copyModified: true });
        return;
    }
    if (movedLocationData && !existingSite.locations?.find(loc => loc.locationId === movedLocationData.locationId)) {
        if (!existingSite.locations) { existingSite.locations = []; }
        existingSite.locations.push(movedLocationData);
    }
};

// --- Handler: MOVE LOCATION (drop inside) ---
const handleMoveLocation = (change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation) => {
    const { sourceNodeId, targetNodeId } = change;

    let movedLocationData = null;
    if (sourceRootLocation) {
        movedLocationData = extractFromHierarchy(sourceRootLocation, sourceNodeId, true);
        if (sourceRootLocation.locationId === sourceNodeId) {
            sourceRootLocation = null;
        } else {
            removeFromHierarchy(sourceRootLocation, sourceNodeId);
        }
    }

    if (targetRootLocation && movedLocationData) {
        updateInHierarchy(targetRootLocation, targetNodeId, (location) => {
            if (!location.subLocations) { location.subLocations = []; }
            location.subLocations.push(movedLocationData);
            location.isModified = true;
        });
    }

    if (existingSite) {
        applyMoveLocationToExistingSite(existingSite, sourceNodeId, sourceRootLocation, targetRootLocation, movedLocationData);
    } else {
        const locations = buildLocationsList(sourceRootLocation, targetRootLocation, movedLocationData);
        modifiedSites.push(createSiteEntry(change.siteId, siteData, locations));
    }
};

// Handle powertrain move when site already exists in modifiedSites
const handlePowertrainExistingSite = (existingSite, sourceRootLocation, targetRootLocation, mergeOpts, getPowertrainIds, powertrainIdsToMove, siteId) => {
    if (sourceRootLocation) {
        mergeRootIntoSite(existingSite, sourceRootLocation, mergeOpts);
    } else {
        existingSite.unAssignedPowertrainIds = [...getPowertrainIds(siteId)];
    }

    if (targetRootLocation) {
        mergeRootIntoSite(existingSite, targetRootLocation, mergeOpts);
    } else {
        existingSite.unAssignedPowertrainIds = [...getPowertrainIds(siteId), ...powertrainIdsToMove];
    }
};

// Handle powertrain move when creating a new site entry
const handlePowertrainNewSite = (modifiedSites, change, siteData, sourceRootLocation, targetRootLocation, powertrainContext) => {
    const { mergeOpts, getPowertrainIds, powertrainIdsToMove } = powertrainContext;
    const updateSiteData = createSiteEntry(change.siteId, siteData, []);

    if (sourceRootLocation && targetRootLocation) {
        if (sourceRootLocation.locationId === targetRootLocation.locationId) {
            mergeLocationHierarchy(sourceRootLocation, targetRootLocation, mergeOpts);
            updateSiteData.locations.push(sourceRootLocation);
        } else {
            updateSiteData.locations.push(sourceRootLocation, targetRootLocation);
        }
    } else if (sourceRootLocation) {
        updateSiteData.locations.push(sourceRootLocation);
        updateSiteData.isModified = true;
        updateSiteData.unAssignedPowertrainIds = [...getPowertrainIds(change.siteId), ...powertrainIdsToMove];
    } else if (targetRootLocation) {
        updateSiteData.locations.push(targetRootLocation);
        updateSiteData.isModified = true;
        updateSiteData.unAssignedPowertrainIds = [...getPowertrainIds(change.siteId)];
    }
    modifiedSites.push(updateSiteData);
};

// --- Handler: MOVE POWERTRAIN ---
const handleMovePowertrain = (change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation) => {
    const { sourceNodeId, targetNodeId, parentId: parentNodeId } = change;

    const powertrainsToMove = change.powertrainsData || [change.draggedNode];
    const powertrainIdsToMove = powertrainsToMove.map(pt => pt?.id || pt?.powertrainId || sourceNodeId);

    const getPowertrainIds = (nodeId) => {
        const nodeData = nodeMap[nodeId];
        let powertrainData = [];
        if (nodeData?.type === NODE_TYPE.SITE) {
            const unassignedNode = nodeData.children?.find(child => child.type === NODE_TYPE.UNASSIGNED_POWERTRAINS);
            powertrainData = unassignedNode?.children?.map(child => child.data) || [];
        } else {
            powertrainData = nodeData?.children
                ?.filter(child => child.type === NODE_TYPE.POWERTRAIN)
                .map(child => child.data) || [];
        }
        return powertrainData
            .filter(pt => !powertrainIdsToMove.includes(pt.powertrainId))
            .map(pt => pt.powertrainId);
    };

    if (sourceRootLocation) {
        updateInHierarchy(sourceRootLocation, parentNodeId, (location) => {
            location.powertrainIds = getPowertrainIds(parentNodeId);
            location.isModified = true;
        });
    }

    if (targetRootLocation) {
        updateInHierarchy(targetRootLocation, targetNodeId, (location) => {
            location.powertrainIds = [...getPowertrainIds(targetNodeId) ?? [], ...powertrainIdsToMove];
            location.isModified = true;
        });
    }

    const mergeOpts = { copyModified: true, copyPowertrainIds: true };

    if (existingSite) {
        handlePowertrainExistingSite(existingSite, sourceRootLocation, targetRootLocation, mergeOpts, getPowertrainIds, powertrainIdsToMove, change.siteId);
    } else {
        handlePowertrainNewSite(modifiedSites, change, siteData, sourceRootLocation, targetRootLocation, { mergeOpts, getPowertrainIds, powertrainIdsToMove });
    }
};

// Handle asset move when site already exists in modifiedSites
const handleAssetExistingSite = (existingSite, sourceRootLocation, targetRootLocation, mergeOpts, getAssetIds, assetIdsToMove, siteId) => {
    if (sourceRootLocation) {
        mergeRootIntoSite(existingSite, sourceRootLocation, mergeOpts);
    } else {
        existingSite.unAssignedAssetIds = [...getAssetIds(siteId)];
    }

    if (targetRootLocation) {
        mergeRootIntoSite(existingSite, targetRootLocation, mergeOpts);
    } else {
        existingSite.unAssignedAssetIds = [...getAssetIds(siteId), ...assetIdsToMove];
    }
};

// Handle asset move when creating a new site entry
const handleAssetNewSite = (modifiedSites, change, siteData, sourceRootLocation, targetRootLocation, assetContext) => {
    const { mergeOpts, getAssetIds, assetIdsToMove } = assetContext;
    const updateSiteData = createSiteEntry(change.siteId, siteData, []);

    if (sourceRootLocation && targetRootLocation) {
        if (sourceRootLocation.locationId === targetRootLocation.locationId) {
            mergeLocationHierarchy(sourceRootLocation, targetRootLocation, mergeOpts);
            updateSiteData.locations.push(sourceRootLocation);
        } else {
            updateSiteData.locations.push(sourceRootLocation, targetRootLocation);
        }
    } else if (sourceRootLocation) {
        updateSiteData.locations.push(sourceRootLocation);
        updateSiteData.isModified = true;
        updateSiteData.unAssignedAssetIds = [...getAssetIds(change.siteId), ...assetIdsToMove];
    } else if (targetRootLocation) {
        updateSiteData.locations.push(targetRootLocation);
        updateSiteData.isModified = true;
        updateSiteData.unAssignedAssetIds = [...getAssetIds(change.siteId)];
    }
    modifiedSites.push(updateSiteData);
};

// --- Handler: MOVE ASSET ---
const handleMoveAsset = (change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation) => {
    const { targetNodeId, parentId: parentNodeId } = change;

    const assetsToMove = change.assetsData || [change.draggedNode];
    const assetIdsToMove = assetsToMove.map(asset => asset.id);
    const parentLocationId = nodeMap[parentNodeId]?.parentId;

    const getAssetIds = (nodeId) => {
        const nodeData = nodeMap[nodeId];
        if (!nodeData) { return []; }
        // Single assets (for both site and location) live in the UNASSIGNED_ASSETS child node,
        // which the tree keeps in sync as assets are moved. Reading data.assets/notConfiguredAssets
        // returns the stale backend snapshot, so sequential one-by-one moves would drop previously
        // moved assets and only keep the last one.
        const unassignedAssetsNode = nodeData.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS);
        const assetData = unassignedAssetsNode?.data?.assets || [];
        return assetData.filter(asset => !assetIdsToMove.includes(asset.id)).map(asset => asset.id);
    };

    if (sourceRootLocation) {
        updateInHierarchy(sourceRootLocation, parentLocationId, (location) => {
            location.assetIds = getAssetIds(parentLocationId);
            location.isModified = true;
        });
    }

    if (targetRootLocation) {
        updateInHierarchy(targetRootLocation, targetNodeId, (location) => {
            location.assetIds = [...getAssetIds(targetNodeId), ...assetIdsToMove];
            location.isModified = true;
        });
    }

    const mergeOpts = { copyModified: true, copyAssetIds: true };

    if (existingSite) {
        handleAssetExistingSite(existingSite, sourceRootLocation, targetRootLocation, mergeOpts, getAssetIds, assetIdsToMove, change.siteId);
    } else {
        handleAssetNewSite(modifiedSites, change, siteData, sourceRootLocation, targetRootLocation, { mergeOpts, getAssetIds, assetIdsToMove });
    }
};

// --- Handler: MOVE (dispatcher) ---
const handleMove = (change, nodeMap, modifiedSites, siteData, existingSite) => {
    const { sourceNodeId, targetNodeId, parentId: parentNodeId, draggedNode: sourceNodeData } = change;
    const sourceType = sourceNodeData?.type;
    const getSourceLocationId = sourceType === NODE_TYPE.LOCATION ? sourceNodeId : parentNodeId;
    const sourceRootLocation = findRootLocation(nodeMap, getSourceLocationId);
    const targetRootLocation = findRootLocation(nodeMap, targetNodeId);

    if (change.position === DROP_POSITION.BEFORE || change.position === DROP_POSITION.AFTER) {
        handleMoveBeforeAfter(change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation);
    } else if (sourceType === NODE_TYPE.LOCATION) {
        handleMoveLocation(change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation);
    } else if (sourceType === NODE_TYPE.POWERTRAIN) {
        handleMovePowertrain(change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation);
    } else if (sourceType === NODE_TYPE.ASSET) {
        handleMoveAsset(change, nodeMap, modifiedSites, siteData, existingSite, sourceRootLocation, targetRootLocation);
    }
};

// Create API request data from pending changes
export const createAPIRequestData = (change, nodeMap, modifiedSites) => {
    if (!change || !nodeMap) { return modifiedSites; }

    const siteData = nodeMap[change.siteId];
    const existingSite = modifiedSites.find(site => site.siteId === change.siteId);

    const handlers = {
        [NODE_MODIFICATION_TYPE.ADD]: handleAdd,
        [NODE_MODIFICATION_TYPE.RENAME]: handleRename,
        [NODE_MODIFICATION_TYPE.DELETE]: handleDelete,
        [NODE_MODIFICATION_TYPE.MOVE]: handleMove,
    };

    const handler = handlers[change.type];
    if (handler) {
        handler(change, nodeMap, modifiedSites, siteData, existingSite);
    }

    return modifiedSites;
};


export const moveAssetsToLocation = (assetIds, sourceId, targetId, nodeMap) => {
    const targetRootLocation = findRootLocation(nodeMap, targetId);
    const sourceRootLocation = findRootLocation(nodeMap, sourceId);
    const siteData = !targetId ? nodeMap[targetId]?.data : !sourceId ? nodeMap[sourceId]?.data : nodeMap[findContainingSite(nodeMap, sourceId)]?.data;
    if (!siteData) { return null; }

    const getAssetIds = (nodeId) => {
        const nodeData = nodeMap[nodeId];
        let assetData = [];
        if (nodeData?.type === NODE_TYPE.SITE) {
            assetData = nodeData?.data?.notConfiguredAssets || [];
        } else {
            assetData = nodeData?.data?.assets || [];
        }
        return assetData.map(asset => asset.id);
    };

    // Remove asset from sourceRootLocation
    if (sourceRootLocation) {
        const removeFromSourceHierarchy = (location) => {
            if (location.locationId === sourceId) {
                // Store remaining asset IDs back to location
                location.assetIds = getAssetIds(sourceId).filter(id => !assetIds.includes(id));
                location.isModified = true;
                return true;
            }
            if (location.subLocations && location.subLocations.length > 0) {
                for (const subLocation of location.subLocations) {
                    if (removeFromSourceHierarchy(subLocation)) {
                        return true;
                    }
                }
            }
            return false;
        };
        removeFromSourceHierarchy(sourceRootLocation);
    }

    // Add asset to targetRootLocation
    if (targetRootLocation) {
        const addToTargetHierarchy = (location) => {
            if (location.locationId === targetId) {
                location.assetIds = [...getAssetIds(location.locationId), ...assetIds];
                location.isModified = true;
                return true;
            }
            if (location.subLocations && location.subLocations.length > 0) {
                for (const subLocation of location.subLocations) {
                    if (addToTargetHierarchy(subLocation)) {
                        return true;
                    }
                }
            }
            return false;
        };
        addToTargetHierarchy(targetRootLocation);
    }

    return {
        siteId: siteData.siteId,
        isModified: !targetRootLocation || !sourceRootLocation,
        rowVersion: siteData.rowVersion,
        locations: sourceRootLocation && targetRootLocation && sourceRootLocation.locationId === targetRootLocation.locationId
            ? [targetRootLocation]
            : [sourceRootLocation, targetRootLocation].filter(Boolean),
        unAssignedAssetIds: !targetRootLocation
            ? [...getAssetIds(targetId), ...assetIds]
            : !sourceRootLocation
                ? getAssetIds(sourceId).filter(id => !assetIds.includes(id))
                : null,
        unAssignedPowertrainIds: null,
    };
};

export const getDownstreamHierarchy = (treeData, nodeId, nodeType) => {
    if (!treeData || nodeId === null) {
        return [];
    }

    const findNode = (nodes) => {
        if (!nodes) { return null; }
        for (const node of nodes) {
            const idMatch = node.id === nodeId;
            const typeMatch = !nodeType || node.data?.type === nodeType;
            if (idMatch && typeMatch) {
                return node;
            }
            if (node.children && node.children.length > 0) {
                const found = findNode(node.children);
                if (found) { return found; }
            }
        }
        return null;
    };

    const matched = findNode(treeData);
    return matched ? [matched] : [];
};

// Helper to find a site in organizations
const findSiteInOrganizations = (orgs, siteId) => {
    if (!Array.isArray(orgs)) {
        orgs = [orgs];
    }

    for (const org of orgs) {
        if (org.sites) {
            for (const site of org.sites) {
                if (site.siteId === siteId) {
                    return site;
                }
            }
        }
        if (org.subOrganizations) {
            const found = findSiteInOrganizations(org.subOrganizations, siteId);
            if (found) { return found; }
        }
    }
    return null;
};

// Helper to search for location in a locations array recursively
const searchLocationsRecursively = (locations, locationId) => {
    if (!locations) { return null; }
    for (const location of locations) {
        if (location.locationId === locationId) {
            return location;
        }
        if (location.subLocations) {
            const found = searchLocationsRecursively(location.subLocations, locationId);
            if (found) { return found; }
        }
    }
    return null;
};

// Helper to find a location within sites
const findLocationInSites = (sites, locationId) => {
    if (!sites) { return null; }
    for (const site of sites) {
        if (site.locations) {
            const found = searchLocationsRecursively(site.locations, locationId);
            if (found) { return found; }
        }
    }
    return null;
};

// Extract site data from site node
const extractSiteNodeData = (selectedNode, orgs) => {
    const siteData = findSiteInOrganizations(orgs, selectedNode.data?.siteId);
    if (!siteData) { return { site: null }; }

    return {
        site: {
            siteId: siteData.siteId,
            siteName: siteData.siteName,
            ...siteData
        }
    };
};

// Extract site and location data from location node
const extractLocationNodeData = (selectedNode, orgs) => {
    const siteId = selectedNode.data?.siteId;
    const locationId = selectedNode.data?.locationId;
    const siteData = findSiteInOrganizations(orgs, siteId);

    if (!siteData) { return { site: null, location: null }; }

    const site = {
        siteId: siteData.siteId,
        siteName: siteData.siteName,
        ...siteData
    };

    const locationData = findLocationInSites([siteData], locationId);
    if (!locationData) { return { site, location: null }; }

    const location = {
        locationId: locationData.locationId,
        locationName: locationData.name || locationData.locationName,
        ...locationData
    };

    return { site, location };
};

// Extract site and optional location from unassigned assets node
const extractUnassignedAssetsNodeData = (selectedNode, orgs) => {
    const siteId = selectedNode.data?.siteId || selectedNode.siteId;
    const locationId = selectedNode.data?.locationId || selectedNode.locationId;
    const siteData = findSiteInOrganizations(orgs, siteId);

    if (!siteData) { return { site: null, location: null }; }

    const site = {
        siteId: siteData.siteId,
        siteName: siteData.siteName,
        ...siteData
    };

    if (!locationId) { return { site, location: null }; }

    const locationData = findLocationInSites([siteData], locationId);
    if (!locationData) { return { site, location: null }; }

    const location = {
        locationId: locationData.locationId,
        locationName: locationData.name || locationData.locationName,
        ...locationData
    };

    return { site, location };
};

export const extractSiteAndLocationFromNode = (selectedNode, hierarchyData) => {
    if (!selectedNode || !hierarchyData) {
        return { site: null, location: null };
    }

    const nodeType = selectedNode?.data?.type;
    const orgs = Array.isArray(hierarchyData) ? hierarchyData : [hierarchyData];

    switch (nodeType) {
        case NODE_TYPE.SITE:
            return extractSiteNodeData(selectedNode, orgs);
        case NODE_TYPE.LOCATION:
            return extractLocationNodeData(selectedNode, orgs);
        case NODE_TYPE.UNASSIGNED_ASSETS:
            return extractUnassignedAssetsNodeData(selectedNode, orgs);
        default:
            return { site: null, location: null };
    }
};
